// packages/shared/src/schemas/widget-config.schema.ts

import { z } from 'zod';
import { METRIC_KEYS } from '../metrics';

export const widgetConfigSchema = z.object({
  // At least one: a report with no metrics is not a report.
  enabledMetrics: z
    .array(z.enum(METRIC_KEYS as [string, ...string[]]))
    .min(1)
    .max(20)
    .optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #1a1a1a')
    .optional(),
  radius: z.number().int().min(0).max(32).optional(),
  font: z.string().max(60).nullable().optional(),
  headline: z.string().max(160).nullable().optional(),
  ctaLabel: z.string().max(40).optional(),
  defaultLocale: z.string().min(2).max(10).optional(),
  /** Removable at white-label L1 and above. */
  showPoweredBy: z.boolean().optional(),
  /** Off until the tenant affirms their jurisdiction permits it. */
  simulationEnabled: z.boolean().optional(),
  bookingMode: z.enum(['deeplink', 'calendly', 'callback', 'native']).optional(),
  bookingUrl: z.string().url().nullable().optional(),
});

/**
 * Compared as scheme + host + port only. A clinic pastes "https://clinic.com/"
 * or a full page URL, and rejecting them for a trailing slash would be a support
 * ticket rather than security.
 */
export const allowedDomainSchema = z.object({
  origin: z
    .string()
    .min(4)
    .max(255)
    .transform((value) => {
      try {
        return new URL(value.startsWith('http') ? value : `https://${value}`).origin.toLowerCase();
      } catch {
        return value;
      }
    })
    .refine((value) => /^https?:\/\/[^/]+$/.test(value), 'That does not look like a website address.'),
});

export const domainSchema = z.object({
  hostname: z
    .string()
    .min(4)
    .max(253)
    .transform((value) => value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''))
    .refine((value) => /^[a-z0-9.-]+\.[a-z]{2,}$/.test(value), 'That does not look like a hostname.')
    .refine((value) => !value.endsWith('skinvizo.com'), 'That domain is already ours.'),
  kind: z.enum(['WIDGET', 'DASHBOARD']).default('WIDGET'),
});

export type WidgetConfigInput = z.infer<typeof widgetConfigSchema>;
export type AllowedDomainInput = z.infer<typeof allowedDomainSchema>;
export type DomainInput = z.infer<typeof domainSchema>;
